// services/pharmacyApi.js
// ---------------------------------------------------------------------------
// Live backend endpoints:
//   GET /api/pharmacy/categories          → [{ id, label, icon, displayOrder }]
//   GET /api/pharmacy/products            → paginated { content[], totalPages, ... }
//       query params: categoryId, medicineType, page, size
//   GET /api/pharmacy/products/search     → paginated { content[], totalPages, ... }
//       query params: categoryId, medicineType, brand, minPrice, maxPrice, q, page, size, sort
//   GET /api/pharmacy/products/brands     → string[]
//       query params: categoryId, medicineType
//   Header: X-Tenant-ID required on all
//
// NOTE: the catalog is now ~124k products (see the new pharmacy dataset), so
// brand/price/search filtering is done SERVER-SIDE via /products/search —
// we no longer pull everything into the browser with size=500.
// ---------------------------------------------------------------------------

import { API_BASE_URL as BASE_URL } from './apiBaseUrl';

const headers = (tenantId) => ({
  'Content-Type': 'application/json',
  'X-Tenant-ID': tenantId,
});

// ---------------------------------------------------------------------------
// Medicine Types — static list matching backend enum values.
// ---------------------------------------------------------------------------
export const getMedicineTypes = () => [
  { id: 'all',        label: 'All Types',   icon: '🏪' },
  { id: 'allopathy',  label: 'Allopathy',   icon: '💊' },
  { id: 'homeopathy', label: 'Homeopathy',  icon: '🌿' },
  { id: 'ayurveda',   label: 'Ayurveda',    icon: '🍃' },
];

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------
export const getCategories = async (tenantId) => {
  const res = await fetch(`${BASE_URL}/pharmacy/categories`, {
    headers: headers(tenantId),
  });
  if (!res.ok) throw new Error(`Failed to fetch categories (${res.status})`);
  const data = await res.json();

  const sorted = [...data].sort((a, b) => a.displayOrder - b.displayOrder);
  return [
    { id: 'all', label: 'All', icon: '🏪' },
    ...sorted,
  ];
};

// ---------------------------------------------------------------------------
// Products
// Only categoryId and medicineType are sent to the backend.
// size=500 ensures we get all products in one call so client-side
// filtering (brand, price, search) works against the full dataset.
// ---------------------------------------------------------------------------
const normaliseProduct = (p) => ({
  id:           p.id,
  name:         p.name,
  brand:        p.brand,
  price:        p.price,
  mrp:          p.mrp,
  inStock:      p.inStock,
  emoji:        p.emoji,
  categoryId:   p.categoryId,
  medicineType: p.medicineType ?? '',
  prescription: p.prescription ?? false,
  unit:         p.unit         ?? '',
  tags:         p.tags         ?? [],
  packaging:    p.packaging    ?? '',
  dosageForm:   p.dosageForm   ?? '',
  imageUrl:     p.imageUrl     ?? null,
});

export const getProducts = async (tenantId, {
  categoryId,
  medicineType,
  page = 0,
  size = 24,
} = {}) => {
  const params = new URLSearchParams();
  if (categoryId   && categoryId   !== 'all') params.set('categoryId',   categoryId);
  if (medicineType && medicineType !== 'all') params.set('medicineType', medicineType);
  params.set('page', page);
  params.set('size', size);

  const res = await fetch(`${BASE_URL}/pharmacy/products?${params}`, {
    headers: headers(tenantId),
  });
  if (!res.ok) throw new Error(`Failed to fetch products (${res.status})`);

  const data = await res.json();
  const items = Array.isArray(data) ? data : (data.content ?? []);
  return {
    items: items.map(normaliseProduct),
    totalPages: data.totalPages ?? 1,
    totalElements: data.totalElements ?? items.length,
  };
};

// ---------------------------------------------------------------------------
// Server-side search — categoryId, medicineType, brand, price range, free-text
// search (q) and sort are all applied on the backend. Required at the current
// catalog size (~124k products); the old client-side filtering approach of
// fetching size=500 no longer covers the dataset.
// ---------------------------------------------------------------------------
export const searchProducts = async (tenantId, {
  categoryId,
  medicineType,
  brand,
  minPrice,
  maxPrice,
  q,
  page = 0,
  size = 24,
  sort,
} = {}) => {
  const params = new URLSearchParams();
  if (categoryId   && categoryId   !== 'all') params.set('categoryId',   categoryId);
  if (medicineType && medicineType !== 'all') params.set('medicineType', medicineType);
  if (brand)                                   params.set('brand',       brand);
  if (minPrice != null)                        params.set('minPrice',    minPrice);
  if (maxPrice != null)                        params.set('maxPrice',    maxPrice);
  if (q)                                       params.set('q',           q);
  if (sort && sort !== 'relevance')            params.set('sort',        sort);
  params.set('page', page);
  params.set('size', size);

  const res = await fetch(`${BASE_URL}/pharmacy/products/search?${params}`, {
    headers: headers(tenantId),
  });
  if (!res.ok) throw new Error(`Failed to search products (${res.status})`);

  const data = await res.json();
  const items = Array.isArray(data) ? data : (data.content ?? []);
  return {
    items: items.map(normaliseProduct),
    totalPages: data.totalPages ?? 1,
    totalElements: data.totalElements ?? items.length,
  };
};

// ---------------------------------------------------------------------------
// Brands — now fetched from the backend (server-side DISTINCT), since deriving
// this client-side would mean pulling the whole 124k-row catalog.
// ---------------------------------------------------------------------------
export const getBrands = async (tenantId, { categoryId, medicineType } = {}) => {
  const params = new URLSearchParams();
  if (categoryId   && categoryId   !== 'all') params.set('categoryId',   categoryId);
  if (medicineType && medicineType !== 'all') params.set('medicineType', medicineType);

  const res = await fetch(`${BASE_URL}/pharmacy/products/brands?${params}`, {
    headers: headers(tenantId),
  });
  if (!res.ok) throw new Error(`Failed to fetch brands (${res.status})`);
  return res.json();
};

// ---------------------------------------------------------------------------
// Order status — GET /api/pharmacy/orders/my
// Returns the logged-in patient's own pharmacy orders (most-recent first per
// the backend), each with { id, status, paymentMode, deliveryAddress,
// totalAmount, createdAt, items[] }. Requires the auth token (not just
// tenant header) since orders are scoped to the authenticated user.
// Currently the only status value the backend ever sets is "PLACED" — there
// is no admin flow yet to progress orders to SHIPPED/DELIVERED/CANCELLED.
// ---------------------------------------------------------------------------
export const getMyOrders = async (token, tenantId) => {
  const res = await fetch(`${BASE_URL}/pharmacy/orders/my`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-Tenant-ID': tenantId,
    },
  });
  if (!res.ok) throw new Error(`Failed to fetch orders (${res.status})`);
  return res.json();
};